// PR-MAY15-LEDGERS-V2 — bank-statement professional design.
//
// Renders the projector output as a real bank statement:
//   - Statement header (entity label + range)
//   - 4 KPI cards (Debit total / Credit total / Net / Lines)
//   - Quick-picker chips: Today / This week / This month / Last month / Custom
//   - Accordion-grouped table (uses LedgerLine.groupKey/groupLabel when set)
//   - "+ Journal entry" + "Download PDF" + (super_admin) trash buttons
//   - Signature block at the bottom for printing
//
// The component is wired into the Vehicle / Driver / Investor profile
// pages exactly as before — it just looks like a real statement now.

import { useEffect, useMemo, useState, type FormEvent, type MouseEvent } from 'react';
import { Loader2, Download, Plus, Trash2, ChevronDown, ChevronRight, FileText, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';
import { useAuth } from '@/lib/auth';

export type LedgerSubjectKind = 'vehicle' | 'driver' | 'investor';

interface LedgerLine {
  id: string;
  postedOn: string;
  description: string;
  debit: number;
  credit: number;
  balance: number;
  sourceTable: string;
  sourceId: string | null;
  sourceCode: string | null;
  groupKey?: string;
  groupLabel?: string;
}
interface LedgerData {
  subjectKind: LedgerSubjectKind;
  subjectId: string;
  subjectLabel: string | null;
  period: { from: string | null; to: string | null };
  summary: {
    openingBalanceSar: number;
    debitSar: number;
    creditSar: number;
    netSar: number;
    closingBalanceSar: number;
    lineCount: number;
    reconciles: boolean;
  };
  totals: { debitSar: number; creditSar: number; balance: number };
  lines: LedgerLine[];
}

interface Props {
  subjectKind: LedgerSubjectKind;
  subjectId: string;
}

const KIND_PATH: Record<LedgerSubjectKind, string> = {
  vehicle: 'vehicles', driver: 'drivers', investor: 'investors',
};

type Preset = 'today' | 'week' | 'month' | 'last_month' | 'custom';

function presetRange(p: Preset): { from?: string; to?: string } {
  const today = new Date();
  const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  if (p === 'today')   return { from: ymd(today), to: ymd(today) };
  if (p === 'week') {
    const monday = new Date(today); const dow = (today.getDay() + 6) % 7; monday.setDate(today.getDate() - dow);
    return { from: ymd(monday), to: ymd(today) };
  }
  if (p === 'month') {
    return { from: ymd(new Date(today.getFullYear(), today.getMonth(), 1)), to: ymd(today) };
  }
  if (p === 'last_month') {
    const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const last  = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from: ymd(first), to: ymd(last) };
  }
  return {};
}

function fmtSar(n: number): string {
  if (!Number.isFinite(n) || n === 0) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function LedgerView({ subjectKind, subjectId }: Props) {
  const { user } = useAuth();
  const role = user?.role ?? '';
  const canPost = ['super_admin', 'admin', 'accountant'].includes(role);
  const canDelete = role === 'super_admin';

  const [data, setData] = useState<LedgerData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preset, setPreset] = useState<Preset>('month');
  const [from, setFrom] = useState<string>('');
  const [to,   setTo]   = useState<string>('');
  const [showJournal, setShowJournal] = useState(false);

  // Apply preset → from/to whenever preset changes (unless custom).
  useEffect(() => {
    if (preset === 'custom') return;
    const r = presetRange(preset);
    setFrom(r.from ?? '');
    setTo(r.to ?? '');
  }, [preset]);

  const url = useMemo(() => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to)   qs.set('to', to);
    const q = qs.toString();
    return `/api/v1/${KIND_PATH[subjectKind]}/${subjectId}/statement${q ? '?' + q : ''}`;
  }, [subjectKind, subjectId, from, to]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await apiClient.get<LedgerData>(url);
      setData(r.data);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [url]);

  async function downloadPdf() {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to)   qs.set('to', to);
    const q = qs.toString();
    const pdfUrl = `/api/v1/${KIND_PATH[subjectKind]}/${subjectId}/statement.pdf${q ? '?' + q : ''}`;
    const token = getStoredToken();
    const resp = await fetch(`${apiClient.defaults.baseURL ?? ''}${pdfUrl}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) { setError(`PDF download failed (HTTP ${resp.status})`); return; }
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
  }

  // Group lines by groupKey when present. Lines without groupKey form a
  // single "—" group. The accordion preserves date sort within each group.
  const grouped = useMemo(() => {
    if (!data) return [] as Array<{ key: string; label: string; lines: LedgerLine[]; totalCredit: number; totalDebit: number }>;
    const map = new Map<string, { key: string; label: string; lines: LedgerLine[]; totalCredit: number; totalDebit: number }>();
    for (const l of data.lines) {
      const k = l.groupKey ?? '__flat__';
      const label = l.groupLabel ?? '';
      let g = map.get(k);
      if (!g) { g = { key: k, label, lines: [], totalCredit: 0, totalDebit: 0 }; map.set(k, g); }
      g.lines.push(l);
      g.totalCredit += l.credit;
      g.totalDebit  += l.debit;
    }
    return Array.from(map.values());
  }, [data]);

  const hasGroups = grouped.length > 0 && grouped.some((g) => g.key !== '__flat__');

  return (
    <div className="space-y-4" data-ledger-view-kind={subjectKind}>
      {/* PR-MAY15-LEDGERS-V2 item 7 — statement header */}
      <div className="rounded-lg border border-border bg-card p-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="inline-flex items-center gap-2">
          <FileText className="h-5 w-5 text-muted-foreground" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
              {subjectKind} statement
            </div>
            <div className="font-medium">{data?.subjectLabel ?? subjectId}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={downloadPdf} disabled={loading || !data} className="gap-1">
            <Download className="h-3.5 w-3.5" /> PDF
          </Button>
          {canPost && (
            <Button size="sm" onClick={() => setShowJournal(true)} className="gap-1" data-ledger-journal-button>
              <Plus className="h-3.5 w-3.5" /> Journal entry
            </Button>
          )}
        </div>
      </div>

      {/* Quick-picker chips + Custom escape hatch */}
      <div className="flex items-center gap-2 flex-wrap">
        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
        {(['today','week','month','last_month','custom'] as Preset[]).map((p) => (
          <button
            type="button"
            key={p}
            onClick={() => setPreset(p)}
            className={
              'px-2.5 py-1 rounded text-xs ' +
              (preset === p
                ? 'bg-foreground text-background'
                : 'bg-muted/40 hover:bg-muted/60 text-muted-foreground')
            }
            data-ledger-preset={p}
          >
            {p === 'today' ? 'Today' : p === 'week' ? 'This week' : p === 'month' ? 'This month' : p === 'last_month' ? 'Last month' : 'Custom'}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="inline-flex items-center gap-1">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-7 w-36 text-xs" />
            <span className="text-muted-foreground text-xs">→</span>
            <Input type="date" value={to}   onChange={(e) => setTo(e.target.value)}   className="h-7 w-36 text-xs" />
          </div>
        )}
        {from && to && preset !== 'custom' && (
          <span className="text-[11px] text-muted-foreground font-mono">{from} → {to}</span>
        )}
      </div>

      {/* PR-MAY15-LEDGERS-V3 item 3 — ultra-summary zone.
          Two rows of KPIs: top row is the bank-statement-style flow
          (Opening → Debit/Credit/Net → Closing), bottom row is meta. */}
      {data && (
        <div className="space-y-2" data-ledger-summary>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Kpi label="Opening balance"  value={fmtSar(data.summary.openingBalanceSar)} tone={data.summary.openingBalanceSar >= 0 ? 'neutral' : 'debit'} />
            <Kpi label="Period debit"     value={fmtSar(data.summary.debitSar)}          tone="debit" />
            <Kpi label="Period credit"    value={fmtSar(data.summary.creditSar)}         tone="credit" />
            <Kpi label="Period net"       value={fmtSar(data.summary.netSar)}            tone={data.summary.netSar >= 0 ? 'credit' : 'debit'} />
            <Kpi label="Closing balance"  value={fmtSar(data.summary.closingBalanceSar)} tone={data.summary.closingBalanceSar >= 0 ? 'credit' : 'debit'} />
          </div>
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>{data.summary.lineCount} line{data.summary.lineCount === 1 ? '' : 's'} in period</span>
            <span data-ledger-reconciliation={data.summary.reconciles ? 'ok' : 'mismatch'}
              className={data.summary.reconciles
                ? 'text-emerald-700 dark:text-emerald-400'
                : 'text-rose-700 dark:text-rose-400 font-medium'}>
              {data.summary.reconciles
                ? '✓ Opening + net = Closing (reconciles)'
                : '✗ Reconciliation mismatch — see audit log'}
            </span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div className="rounded border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</div>
      ) : !data || data.lines.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/10 py-10 text-center text-sm text-muted-foreground">
          No entries in this window.
        </div>
      ) : hasGroups ? (
        <div className="space-y-2">
          {grouped.map((g) => (
            <GroupedSection key={g.key} group={g} canDelete={canDelete} onDeleted={load} />
          ))}
        </div>
      ) : (
        <LinesTable lines={grouped[0]?.lines ?? []} canDelete={canDelete} onDeleted={load} />
      )}

      {/* Signature block (visible in print + PDF context). */}
      {data && data.lines.length > 0 && (
        <div className="grid grid-cols-2 gap-8 pt-8 mt-6 border-t border-border print:break-inside-avoid">
          <SignatureBlock label="Prepared by" />
          <SignatureBlock label="Approved by" />
        </div>
      )}

      {canPost && (
        <JournalDrawer
          open={showJournal}
          onOpenChange={setShowJournal}
          subjectKind={subjectKind}
          subjectId={subjectId}
          onPosted={() => { setShowJournal(false); load(); }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone: 'debit' | 'credit' | 'neutral' }) {
  const cls = tone === 'debit'  ? 'text-rose-700 dark:text-rose-400 bg-rose-500/5 border-rose-500/20'
            : tone === 'credit' ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/5 border-emerald-500/20'
            : 'text-muted-foreground bg-muted/30 border-border';
  return (
    <div className={'rounded-lg border p-3 ' + cls}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-base font-mono mt-1">{value}</div>
    </div>
  );
}

function SignatureBlock({ label }: { label: string }) {
  return (
    <div>
      <div className="border-b border-foreground/50 mb-1" style={{ minHeight: '40px' }} />
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    </div>
  );
}

function GroupedSection({ group, canDelete, onDeleted }: {
  group: { key: string; label: string; lines: LedgerLine[]; totalCredit: number; totalDebit: number };
  canDelete: boolean;
  onDeleted: () => void;
}) {
  const [open, setOpen] = useState(true);
  const net = group.totalCredit - group.totalDebit;
  return (
    <div className="rounded-lg border border-border overflow-hidden" data-ledger-group={group.key}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="w-full px-3 py-2 bg-muted/30 flex items-center justify-between text-sm hover:bg-muted/40"
      >
        <div className="inline-flex items-center gap-2">
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          <span className="font-medium">{group.label || 'Unassigned'}</span>
          <span className="text-xs text-muted-foreground">({group.lines.length} lines)</span>
        </div>
        <div className="text-xs font-mono">
          D: {fmtSar(group.totalDebit)} · C: {fmtSar(group.totalCredit)} ·
          <span className={net >= 0 ? ' text-emerald-700 dark:text-emerald-400' : ' text-rose-700 dark:text-rose-400'}>
            {' '}Net {fmtSar(net)}
          </span>
        </div>
      </button>
      {open && <LinesTable lines={group.lines} canDelete={canDelete} onDeleted={onDeleted} compact />}
    </div>
  );
}

function LinesTable({ lines, canDelete, onDeleted, compact }: {
  lines: LedgerLine[]; canDelete: boolean; onDeleted: () => void; compact?: boolean;
}) {
  return (
    <div className={compact ? '' : 'rounded-lg border border-border overflow-hidden'}>
      <table className="min-w-full text-sm">
        <thead className="text-xs text-muted-foreground bg-muted/30 border-b border-border">
          <tr>
            <th className="text-left px-3 py-2 w-24">Date</th>
            <th className="text-left px-3 py-2">Description</th>
            <th className="text-right px-3 py-2 w-24">Debit</th>
            <th className="text-right px-3 py-2 w-24">Credit</th>
            <th className="text-right px-3 py-2 w-28">Balance</th>
            {canDelete && <th className="px-2 py-2 w-8" />}
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.id} className="border-b border-border last:border-b-0 hover:bg-muted/20" data-ledger-row>
              <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{l.postedOn}</td>
              <td className="px-3 py-2">
                {l.description}
                {l.sourceCode && <span className="ml-2 text-[11px] text-muted-foreground font-mono">{l.sourceCode}</span>}
              </td>
              <td className={'px-3 py-2 text-right font-mono ' + (l.debit ? 'text-rose-700 dark:text-rose-400' : 'text-muted-foreground')}>
                {fmtSar(l.debit)}
              </td>
              <td className={'px-3 py-2 text-right font-mono ' + (l.credit ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground')}>
                {fmtSar(l.credit)}
              </td>
              <td className={'px-3 py-2 text-right font-mono ' + (l.balance < 0 ? 'text-rose-700 dark:text-rose-400' : '')}>
                {fmtSar(l.balance)}
              </td>
              {canDelete && (
                <td className="px-2 py-2">
                  {l.sourceTable === 'manual' && (
                    <ManualDeleteButton id={l.sourceId!} onDeleted={onDeleted} />
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ManualDeleteButton({ id, onDeleted }: { id: string; onDeleted: () => void }) {
  const [busy, setBusy] = useState(false);
  async function go(e: MouseEvent) {
    e.stopPropagation();
    if (!confirm('Delete this manual journal entry? Audit-irreversible — consider a reversing entry instead.')) return;
    setBusy(true);
    try {
      await apiClient.delete(`/api/v1/statement-entries/${id}`);
      onDeleted();
    } finally { setBusy(false); }
  }
  return (
    <button type="button" onClick={go} disabled={busy} title="Delete journal entry (super_admin only)"
      className="text-rose-700 dark:text-rose-400 hover:bg-rose-500/10 p-1 rounded"
      data-ledger-delete-entry>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
    </button>
  );
}

function JournalDrawer({
  open, onOpenChange, subjectKind, subjectId, onPosted,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  subjectKind: LedgerSubjectKind;
  subjectId: string;
  onPosted: () => void;
}) {
  const [postedOn, setPostedOn]   = useState<string>(new Date().toISOString().slice(0, 10));
  const [side, setSide]           = useState<'debit' | 'credit'>('debit');
  const [amount, setAmount]       = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (description.trim().length < 3) { setErr('Description must be at least 3 characters'); return; }
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) { setErr('Amount must be a positive number'); return; }
    setBusy(true);
    try {
      await apiClient.post('/api/v1/statement-entries', {
        subjectKind, subjectId,
        postedOn,
        debitSar:  side === 'debit'  ? amt : 0,
        creditSar: side === 'credit' ? amt : 0,
        description: description.trim(),
      });
      setAmount(''); setDescription('');
      onPosted();
    } catch (err) {
      // PR-MAY15-LEDGERS-V2 item 5 — surface server-side validation message
      // when present (the API now puts a friendly string in `.message`).
      const msg =
        (err as { response?: { data?: { message?: string; error?: string } } }).response?.data?.message ??
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ??
        extractErrorMessage(err);
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>New journal entry</SheetTitle>
          <SheetDescription>Manual {subjectKind} ledger line. Recorded against your user; can't be edited afterwards.</SheetDescription>
        </SheetHeader>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="je-date">Posted on</Label>
            <Input id="je-date" type="date" value={postedOn} onChange={(e) => setPostedOn(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Side</Label>
            <div className="inline-flex rounded-md border border-input overflow-hidden">
              <button type="button" onClick={() => setSide('debit')}
                className={'px-3 py-1.5 text-sm ' + (side === 'debit' ? 'bg-rose-500/15 text-rose-700 dark:text-rose-400' : 'text-muted-foreground')}>
                Debit
              </button>
              <button type="button" onClick={() => setSide('credit')}
                className={'px-3 py-1.5 text-sm ' + (side === 'credit' ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground')}>
                Credit
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="je-amount">Amount (SAR)</Label>
            <Input id="je-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="je-desc">Description</Label>
            <textarea id="je-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          {err && <div className="rounded border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">{err}</div>}
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={busy} data-ledger-journal-submit>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Post entry'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
